import React, { FC, useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import styled from 'styled-components';
import {
  Backdrop,
  ButtonTertiary,
  DialogAlert,
  LoadingIndicator,
  spacer16,
  spacer40,
  Type,
} from '@spotify-internal/encore-web';
import { DeliveryInfoDialog } from '@spotify-internal/encore-web/content-platform/components/DeliveryInfoDialog';
import { useT } from '@spotify-internal/i18n-core';
import { sendEvent } from '@spotify-internal/s4mp-page-analytics';
import { useProductXml } from '@hooks/useProductXml';
import { useProductXmlDeliveredFiles } from '@hooks/useProductXmlDeliveredFiles';
import { LoadingError } from '@components/shared/LoadingError';
import { EVENT_ACTION, EVENT_CATEGORY } from '@lib/ga/types';
import { GA_PRODUCT_CATALOG } from '@helpers/constants';

const CenteredContainer = styled.div`
  padding: ${spacer40} ${spacer16};
  text-align: center;
`;

type XmlDialogProps = {
  feedGid: string;
  deliveryId: string;
  productId: string;
  footer: React.ReactNode;
  onClose: () => void;
};

export const XmlDialog: FC<XmlDialogProps> = ({
  feedGid,
  deliveryId,
  productId,
  footer,
  onClose,
}: XmlDialogProps) => {
  const t = useT();
  const router = useRouter();
  const { id: albumUri } = router.query;

  const {
    data: xmlData,
    error: xmlError,
    isValidating: isProductXmlDataValidating,
    actions: { getXmlData },
  } = useProductXml(
    typeof albumUri === 'string' ? albumUri : '',
    feedGid,
    deliveryId,
    productId,
  );

  const {
    data: xmlDeliveredFilesData,
    error: xmlDeliveredFilesError,
    isValidating: isXmlDeliveredFilesDataValidating,
  } = useProductXmlDeliveredFiles(
    typeof albumUri === 'string' ? albumUri : '',
    feedGid,
    deliveryId,
    productId,
  );

  useEffect(() => {
    getXmlData(deliveryId);
  }, [deliveryId, getXmlData]);

  const renderFilesFoundText = () => {
    const filesCount = xmlDeliveredFilesData?.files?.length;
    if (filesCount === 1) {
      return t(
        'atlas.count.file_found',
        '1 file found',
        'Indicates 1 file (singular) was found',
      );
    }
    return t(
      'atlas.count.files_found',
      `{filesCount} files found`,
      `Indicates "X" number of files (plural) were found`,
      {
        filesCount,
      },
    );
  };

  if (xmlError) {
    return (
      <Backdrop center>
        <DialogAlert
          body={
            <CenteredContainer>
              <LoadingError />
            </CenteredContainer>
          }
          footer={footer}
        />
      </Backdrop>
    );
  }
  if (
    isProductXmlDataValidating ||
    isXmlDeliveredFilesDataValidating ||
    !xmlData
  ) {
    return (
      <Backdrop center>
        <DialogAlert
          body={
            <CenteredContainer>
              <LoadingIndicator data-testid="loading" />
            </CenteredContainer>
          }
          footer={footer}
        />
      </Backdrop>
    );
  }

  return (
    <DeliveryInfoDialog
      xml={xmlData.data}
      title={xmlData.fileName}
      fileNames={
        xmlDeliveredFilesError === null && xmlDeliveredFilesData?.files.length
          ? xmlDeliveredFilesData.files
          : []
      }
      downloadLabel={t(
        'atlas.actions.download_xml',
        'Download XML',
        `Action to download XML file. Keep the string 'XML' as is.`,
      )}
      primaryReleaseIdLabel={t(
        'atlas.labels.primary_release_id',
        'Primary release ID',
        'Label for Primary release ID',
      )}
      primaryReleaseId={productId}
      batchIdLabel={t(
        'atlas.labels.batch_id',
        'Batch ID',
        'Label for Batch ID',
      )}
      batchId={deliveryId}
      xmlTabLabel="XML"
      filesTabLabel={t('atlas.labels.files', 'Files', 'Label for Files')}
      filesFoundLabel={
        xmlDeliveredFilesError === null && xmlDeliveredFilesData?.files.length
          ? renderFilesFoundText()
          : t(
              'atlas.errors.theres_a_problem',
              `There's a problem on our end and we can't show your content right now. Check back soon.`,
              'General error message',
            )
      }
      closeLabel={t('atlas.actions.close', 'Close', 'Action to close')}
      onClose={onClose}
    />
  );
};

const CloseXmlDialogButton = styled(ButtonTertiary)`
  cursor: pointer;
`;

const ViewDeliveryXmlButton = styled(ButtonTertiary)`
  cursor: pointer;
  font-weight: normal;
  white-space: nowrap;
`;

type DeliveryXmlContentProps = {
  feedGid: string;
  deliveryId: string;
  productId: string;
};

const DeliveryXmlContent: FC<DeliveryXmlContentProps> = ({
  feedGid,
  deliveryId,
  productId,
}: DeliveryXmlContentProps) => {
  const t = useT();
  const [dialogVisible, setDialogVisible] = useState(false);

  const handleOpenClick = () => {
    setDialogVisible(true);
    sendEvent({
      eventCategory: EVENT_CATEGORY.VIEW_DELIVERY_XML_BUTTON,
      eventAction: EVENT_ACTION.CLICK,
      eventLabel: 'Opened delivery XML modal',
      product: GA_PRODUCT_CATALOG,
    });
  };
  const handleCloseClick = () => setDialogVisible(false);

  return (
    <>
      <ViewDeliveryXmlButton onClick={handleOpenClick}>
        <Type semanticColor="textBrightAccent">
          {t(
            'atlas.actions.view_xml',
            'View XML',
            `Action to view XML by clicking a button to open a modal with more details. Keep the string 'XML' as is.`,
          )}
        </Type>
      </ViewDeliveryXmlButton>
      {dialogVisible && (
        <XmlDialog
          feedGid={feedGid}
          deliveryId={deliveryId}
          productId={productId}
          footer={
            <CloseXmlDialogButton
              buttonSize={ButtonTertiary.md}
              semanticColor="textBrightAccent"
              condensed
              onClick={handleCloseClick}
            >
              {t('atlas.actions.close', 'Close', 'Action to close')}
            </CloseXmlDialogButton>
          }
          onClose={handleCloseClick}
        />
      )}
    </>
  );
};

export default DeliveryXmlContent;
